import { useEffect, useState } from 'react';
import SectionHead from '../../components/common/SectionHead';
import Card from '../../components/common/Card';
import Field from '../../components/common/Field';
import Avatar from '../../components/common/Avatar';
import EmptyState from '../../components/common/EmptyState';
import Spinner from '../../components/common/Spinner';
import useProfileStore, { readProfile, dobOf, tagLabel } from '../../store/useProfileStore';
import useMyProfileStore from '../../store/useMyProfileStore';
import { showToast } from '../../store/useToastStore';
import { fmtDate, kidAge, todayISO } from '../../utils/formatters';
import { IconTrash } from '../../components/icons';

const ROLES = ['Mom', 'Dad', 'Parent', 'Guardian', 'Grandparent', 'Caregiver'];
const GENDERS = [
  { value: 'boy', label: 'Boy' },
  { value: 'girl', label: 'Girl' },
  { value: 'other', label: 'Other' },
];
const MAX_INTERESTS = 10;

const initialsOf = (p) =>
  ((p.firstName[0] || '') + (p.lastName[0] || '')).toUpperCase() || (p.name[0] || '?').toUpperCase();

export default function ProfilePage() {
  const me = useMyProfileStore((s) => s.me);
  const loaded = useMyProfileStore((s) => s.loaded);
  const { saving, interestOptions, interestsLoading, loadInterestOptions, save } = useProfileStore();

  const [nameFormOpen, setNameFormOpen] = useState(false);
  const [nameDraft, setNameDraft] = useState({});
  const [bioFormOpen, setBioFormOpen] = useState(false);
  const [bioDraft, setBioDraft] = useState('');
  const [kidFormOpen, setKidFormOpen] = useState(false);
  const [kidDraft, setKidDraft] = useState({ name: '', gender: 'boy', dob: '' });
  const [intFormOpen, setIntFormOpen] = useState(false);
  const [interestsDraft, setInterestsDraft] = useState([]);

  useEffect(() => {
    loadInterestOptions().catch((err) => showToast(err.message));
  }, [loadInterestOptions]);

  if (!loaded) {
    return (
      <div className="sec on" style={{ display: 'grid', placeItems: 'center', minHeight: 240 }}>
        <Spinner variant="green" />
      </div>
    );
  }

  const profile = readProfile(me);

  /* One helper for every edit: close the form on success, keep it open and say
     why on failure — nothing on this page edits locally any more. */
  const commit = async (patch, message, closeForm) => {
    try {
      await save(patch);
      closeForm();
      showToast(message);
    } catch (err) {
      showToast(err.message);
    }
  };

  /* ── name / location ── */
  const openNameForm = () => {
    setNameDraft({
      firstName: profile.firstName,
      lastName: profile.lastName,
      zip: profile.zip,
      role: profile.role || 'Parent',
    });
    setNameFormOpen((o) => !o);
  };
  const submitName = () => {
    if (!nameDraft.firstName?.trim()) { showToast('Enter your first name.'); return; }
    commit(
      {
        firstName: nameDraft.firstName.trim(),
        lastName: nameDraft.lastName.trim(),
        zip: nameDraft.zip.trim(),
        role: nameDraft.role,
      },
      'Profile updated ✓',
      () => setNameFormOpen(false)
    );
  };

  /* ── bio ── */
  const openBioForm = () => {
    setBioDraft(profile.bio);
    setBioFormOpen((o) => !o);
  };
  const submitBio = () => commit({ bio: bioDraft.trim() }, 'Bio saved ✓', () => setBioFormOpen(false));

  /* ── kids ── */
  const closeKidForm = () => {
    setKidFormOpen(false);
    setKidDraft({ name: '', gender: 'boy', dob: '' });
  };
  /* bare native date input (typeable, own calendar icon) — see AddChildModal */
  const dobProblem = kidDraft.dob && kidDraft.dob > todayISO() ? "Birth date can't be in the future." : '';
  const submitKid = () => {
    if (!kidDraft.name.trim() || !kidDraft.dob) { showToast("Enter the child's name and birth date."); return; }
    if (dobProblem) { showToast(dobProblem); return; }
    const kid = {
      id: `${Date.now()}`,
      childName: kidDraft.name.trim(),
      gender: kidDraft.gender,
      birthDate: new Date(`${kidDraft.dob}T00:00:00`).toISOString(),
      imagePath: '',
    };
    commit({ kids: [...profile.kids, kid] }, `${kid.childName} added ✓`, closeKidForm);
  };
  const deleteKid = (id) => {
    const gone = profile.kids.find((k) => k.id === id);
    commit({ kids: profile.kids.filter((k) => k.id !== id) }, `${gone?.childName || 'Child'} removed ✓`, () => {});
  };

  /* ── interests ──
     Stored tags carry their emoji; the chips come from the server's own list, so
     a tag written here matches what the phone app writes. */
  const allInterests = interestOptions.flatMap((c) => c.items);
  const openIntForm = () => {
    const picked = new Set(profile.interests.map(tagLabel));
    setInterestsDraft(allInterests.filter((i) => picked.has(i.label)).map((i) => i.id));
    setIntFormOpen((o) => !o);
  };
  const toggleInterest = (id) => {
    setInterestsDraft((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= MAX_INTERESTS) return prev;
      return [...prev, id];
    });
  };
  const submitInterests = () => {
    const picked = interestsDraft.map((id) => allInterests.find((i) => i.id === id)).filter(Boolean);
    commit(
      { interests: picked.map((i) => `${i.emoji || ''} ${i.label}`.replace(/\s+/g, ' ').trim()) },
      'Interests updated ✓',
      () => setIntFormOpen(false)
    );
  };

  const sp = profile.spouseInvite;
  const invitedBy = profile.invitedBy;

  return (
    <div className="sec on">
      <SectionHead
        title="My Profile"
        sub="Same profile parents see across the app — kids, school groups, and interests, all in one place."
      />

      <Card>
        <div className="prof-head">
          <Avatar
            src={profile.photo || undefined}
            initials={initialsOf(profile)}
            color="linear-gradient(145deg,#1e3e2b,#4fa472)"
            size={72}
            fontSize={26}
          />
          <div className="info">
            <div className="nm">{profile.name || 'Unnamed'}</div>
            <div className="loc">
              {[profile.location?.address || profile.location?.name, profile.zip, profile.role]
                .filter(Boolean)
                .join(' · ') || 'No location set'}
            </div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={openNameForm}>Edit</button>
        </div>
        <div className={`inline-form ${nameFormOpen ? 'on' : ''}`} style={{ marginTop: 14 }}>
          <div className="form-grid">
            <Field label="First Name">
              <input type="text" value={nameDraft.firstName || ''} onChange={(e) => setNameDraft({ ...nameDraft, firstName: e.target.value })} />
            </Field>
            <Field label="Last Name">
              <input type="text" value={nameDraft.lastName || ''} onChange={(e) => setNameDraft({ ...nameDraft, lastName: e.target.value })} />
            </Field>
            <Field label="Zip Code">
              <input type="text" maxLength={10} value={nameDraft.zip || ''} onChange={(e) => setNameDraft({ ...nameDraft, zip: e.target.value })} />
            </Field>
            <Field label="Parenting Role">
              <select value={nameDraft.role || 'Parent'} onChange={(e) => setNameDraft({ ...nameDraft, role: e.target.value })}>
                {/* whatever the account already carries stays selectable */}
                {[...new Set([...(profile.role ? [profile.role] : []), ...ROLES])].map((r) => (
                  <option key={r}>{r}</option>
                ))}
              </select>
            </Field>
          </div>
          <div className="inline-form-actions">
            <button className="btn btn-ghost" onClick={() => setNameFormOpen(false)}>Cancel</button>
            <button className="btn btn-primary" disabled={saving} onClick={submitName}>
              {saving ? <><Spinner /> Saving…</> : 'Save'}
            </button>
          </div>
        </div>
      </Card>

      <Card title="Bio" action={<button className="btn btn-ghost btn-sm" onClick={openBioForm}>Edit</button>}>
        <p style={{ fontSize: 13, color: 'var(--body)', lineHeight: 1.65, margin: 0 }}>
          {profile.bio || 'No bio added yet.'}
        </p>
        <div className={`inline-form ${bioFormOpen ? 'on' : ''}`} style={{ marginTop: 12 }}>
          <textarea
            rows={4}
            maxLength={500}
            value={bioDraft}
            style={{ marginBottom: 4 }}
            onChange={(e) => setBioDraft(e.target.value)}
          />
          <div className="hint" style={{ textAlign: 'right', marginBottom: 10 }}>{bioDraft.length} / 500</div>
          <div className="inline-form-actions">
            <button className="btn btn-ghost" onClick={() => setBioFormOpen(false)}>Cancel</button>
            <button className="btn btn-primary" disabled={saving} onClick={submitBio}>
              {saving ? <><Spinner /> Saving…</> : 'Save'}
            </button>
          </div>
        </div>
      </Card>

      <Card title="My Kids" action={<button className="btn btn-primary btn-sm" onClick={() => setKidFormOpen((o) => !o)}>+ Add Child</button>}>
        <div className={`inline-form ${kidFormOpen ? 'on' : ''}`}>
          <div className="form-grid">
            <Field label="Child's Name">
              <input type="text" value={kidDraft.name} placeholder="e.g. Cooper" onChange={(e) => setKidDraft({ ...kidDraft, name: e.target.value })} />
            </Field>
            <Field label="Gender">
              <select value={kidDraft.gender} onChange={(e) => setKidDraft({ ...kidDraft, gender: e.target.value })}>
                {GENDERS.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
              </select>
            </Field>
            <Field
              label="Date of Birth"
              className={dobProblem ? 'missing' : ''}
              hint={dobProblem ? <span style={{ color: 'var(--red)' }}>{dobProblem}</span> : undefined}
            >
              <input
                type="date"
                value={kidDraft.dob}
                max={todayISO()}
                onChange={(e) => setKidDraft({ ...kidDraft, dob: e.target.value })}
              />
            </Field>
          </div>
          <div className="inline-form-actions">
            <button className="btn btn-ghost" onClick={closeKidForm}>Cancel</button>
            <button className="btn btn-primary" disabled={saving} onClick={submitKid}>
              {saving ? <><Spinner /> Saving…</> : 'Add Child'}
            </button>
          </div>
        </div>
        <div>
          {profile.kids.length ? (
            profile.kids.map((k) => (
              <div className="kid-row-card" key={k.id}>
                {k.imagePath ? (
                  <Avatar src={k.imagePath} size={38} style={{ borderRadius: '50%' }} />
                ) : (
                  <div className="avatar" style={{ width: 38, height: 38, fontSize: 13, background: 'var(--green-light)', color: 'var(--green)' }}>
                    {(k.childName || '?').slice(0, 2).toUpperCase()}
                  </div>
                )}
                <div className="info">
                  <div className="nm">{k.childName}</div>
                  <div className="dob">{dobOf(k) ? fmtDate(dobOf(k)) : '—'}</div>
                </div>
                {dobOf(k) && <div className="kid-age-badge">{kidAge(dobOf(k))} yrs</div>}
                <div className="ev-trash" onClick={() => deleteKid(k.id)}>
                  <IconTrash />
                </div>
              </div>
            ))
          ) : (
            <EmptyState>No kids added yet.</EmptyState>
          )}
        </div>
      </Card>

      {/* Which school groups an account belongs to is set when joining them, not
          from here — the app has no way to change it afterwards. */}
      <Card title="School Groups">
        <div>
          {profile.schools.length ? (
            profile.schools.map((s) => (
              <div className="school-edit-block" key={s._id}>
                <div className="school-edit-label">
                  <span className="ui-inline-icon"><img src="/assets/icons/school.svg" alt="" /></span>{s.name}
                </div>
                {(s.classes || []).length ? (
                  s.classes.map((c) => (
                    <span className="grade-chip-display" key={c._id}>{c.name}</span>
                  ))
                ) : (
                  <span style={{ fontSize: 12, color: 'var(--muted)' }}>No classes joined at this school.</span>
                )}
              </div>
            ))
          ) : (
            <EmptyState>Not part of a school group yet.</EmptyState>
          )}
        </div>
      </Card>

      <Card title="Interests" action={<button className="btn btn-ghost btn-sm" onClick={openIntForm}>Edit</button>}>
        <div className="tag-select-row">
          {profile.interests.length ? (
            profile.interests.map((t) => <span className="src-chip-btn on" key={t}>{t}</span>)
          ) : (
            <EmptyState>No interests selected yet.</EmptyState>
          )}
        </div>
        <div className={`inline-form ${intFormOpen ? 'on' : ''}`} style={{ marginTop: 12 }}>
          {interestsLoading && !allInterests.length ? (
            <div className="hint" style={{ marginBottom: 10 }}><Spinner variant="green" /> Loading interests…</div>
          ) : (
            interestOptions.map((cat) => (
              <div key={cat.id} style={{ marginBottom: 12 }}>
                <div className="hint" style={{ marginBottom: 6, fontWeight: 700, color: 'var(--ink)' }}>{cat.label}</div>
                <div className="tag-select-row">
                  {cat.items.map((item) => {
                    const on = interestsDraft.includes(item.id);
                    return (
                      <span
                        key={item.id}
                        className={`src-chip-btn ${on ? 'on' : ''}`}
                        style={!on && interestsDraft.length >= MAX_INTERESTS ? { opacity: 0.45 } : undefined}
                        onClick={() => toggleInterest(item.id)}
                      >
                        {item.emoji} {item.label}
                      </span>
                    );
                  })}
                </div>
              </div>
            ))
          )}
          <div className="hint" style={{ marginBottom: 10 }}>{interestsDraft.length} / {MAX_INTERESTS} selected</div>
          <div className="inline-form-actions">
            <button className="btn btn-ghost" onClick={() => setIntFormOpen(false)}>Cancel</button>
            <button className="btn btn-primary" disabled={saving} onClick={submitInterests}>
              {saving ? <><Spinner /> Saving…</> : 'Save'}
            </button>
          </div>
        </div>
      </Card>

      <Card title="Spouse / Partner">
        <div>
          {sp ? (
            <div className="spouse-card">
              <div className="ic"><img src="/assets/icons/partner.svg" alt="" /></div>
              <div className="info">
                <div className="nm">{[sp.CountryCode, sp.PhoneNo].filter(Boolean).join(' ')}</div>
                <div className="st">{sp.status === 'joined' ? '✓ Linked' : 'Invite pending'}</div>
              </div>
            </div>
          ) : invitedBy ? (
            <div className="spouse-card">
              <div className="ic"><img src="/assets/icons/partner.svg" alt="" /></div>
              <div className="info">
                <div className="nm">{invitedBy.invitedByUser?.name || 'Partner'}</div>
                <div className="st">
                  {invitedBy.invitedByUser?.phone_number ? `${invitedBy.invitedByUser.phone_number} · ` : ''}
                  Invited you
                </div>
              </div>
            </div>
          ) : (
            <div className="spouse-card">
              <div className="ic"><img src="/assets/icons/partner.svg" alt="" /></div>
              <div className="info">
                <div className="nm">No spouse linked</div>
                <div className="st">Not added during onboarding</div>
              </div>
            </div>
          )}
        </div>
        <div className="hint" style={{ marginTop: 10 }}>
          Spouse linking happens once, during onboarding sign-up — there's currently no way to add or change it
          afterward, so it's shown here for reference only.
        </div>
      </Card>
    </div>
  );
}
